import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import PartnerModal from "../../components/PartnerModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_PARTNERS") {
    const partners = action.payload;
    const newPartners = [];

    partners.forEach(partner => {
      const partnerIndex = state.findIndex(p => p.id === partner.id);
      if (partnerIndex !== -1) {
        state[partnerIndex] = partner;
      } else {
        newPartners.push(partner);
      }
    });

    return [...state, ...newPartners];
  }

  if (action.type === "UPDATE_PARTNERS") {
    const partner = action.payload;
    const partnerIndex = state.findIndex(p => p.id === partner.id);

    if (partnerIndex !== -1) {
      state[partnerIndex] = partner;
      return [...state];
    }
    return [partner, ...state];
  }

  if (action.type === "DELETE_PARTNERS") {
    const partnerId = action.payload;
    return state.filter(partner => partner.id !== partnerId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  }
}));

const Partners = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [deletingPartner, setDeletingPartner] = useState(null);
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [partners, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    if (!user.super) {
      toast.error(
        "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
      );
      setTimeout(() => {
        history.push(`/`);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchPartners = async () => {
        try {
          const { data } = await api.get("/partners", {
            params: { searchParam, pageNumber }
          });
          dispatch({ type: "LOAD_PARTNERS", payload: data.records });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchPartners();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const handleOpenPartnerModal = () => {
    setSelectedPartner(null);
    setPartnerModalOpen(true);
  };

  const handleClosePartnerModal = () => {
    setSelectedPartner(null);
    setPartnerModalOpen(false);
    dispatch({ type: "RESET" });
    setPageNumber(1);
  };

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditPartner = partner => {
    setSelectedPartner(partner);
    setPartnerModalOpen(true);
  };

  const handleDeletePartner = async partnerId => {
    try {
      await api.delete(`/partners/${partnerId}`);
      toast.success(i18n.t("partners.toasts.deleted"));
      dispatch({ type: "DELETE_PARTNERS", payload: partnerId });
    } catch (err) {
      toastError(err);
    }
    setDeletingPartner(null);
  };

  const loadMore = () => {
    setPageNumber(prevPageNumber => prevPageNumber + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingPartner && `${i18n.t("partners.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeletePartner(deletingPartner.id)}
      >
        {i18n.t("partners.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <PartnerModal
        open={partnerModalOpen}
        onClose={handleClosePartnerModal}
        partnerId={selectedPartner && selectedPartner.id}
      />
      <MainHeader>
        <Title>{i18n.t("partners.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              )
            }}
          />
          <Button variant="contained" color="primary" onClick={handleOpenPartnerModal}>
            {i18n.t("partners.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("partners.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("partners.table.isDefault")}</TableCell>
              <TableCell align="center">{i18n.t("partners.table.phone")}</TableCell>
              <TableCell align="center">{i18n.t("partners.table.email")}</TableCell>
              <TableCell align="center">{i18n.t("partners.table.referralCode")}</TableCell>
              <TableCell align="center">{i18n.t("partners.table.link")}</TableCell>
              <TableCell align="center">{i18n.t("partners.table.commission")}</TableCell>
              <TableCell align="center">{i18n.t("partners.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {partners.map(partner => (
                <TableRow key={partner.id}>
                  <TableCell align="center">{partner.name}</TableCell>
                  <TableCell align="center">
                    {partner.isDefault ? i18n.t("partners.table.isDefaultYes") : ""}
                  </TableCell>
                  <TableCell align="center">{partner.phone}</TableCell>
                  <TableCell align="center">{partner.email}</TableCell>
                  <TableCell align="center">{partner.referralCode}</TableCell>
                  <TableCell align="center">{partner.link}</TableCell>
                  <TableCell align="center">
                    {partner.commission}
                    {partner.typeCommission === "percentual" ? "%" : ""}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditPartner(partner)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingPartner(partner);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {loading &&
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRowSkeleton key={`skeleton-${idx}`} columns={8} />
                ))}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Partners;
